import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {Element | null} */
  #categoryHeader = null;
  /** @type {HTMLElement | null} */
  #categoryTrigger = null;
  #categoryFrame = 0;
  #categoryRequest = 0;

  /**
   * Open a category through the same native drawer controls as the hamburger.
   * @param {MouseEvent} event
   */
  #onCategoryClick = (event) => {
    if (!window.matchMedia('(max-width: 749px)').matches || !(event.target instanceof Element)) return;
    const trigger = event.target.closest('.menu-list--mobile [data-mobile-menu-handle]');
    if (!(trigger instanceof HTMLElement)) return;
    const handle = trigger.dataset.mobileMenuHandle;
    if (!handle) return;
    const summary = this.querySelector('#HeaderDrawer-' + CSS.escape(handle));
    if (!(summary instanceof HTMLElement) || summary.tagName !== 'SUMMARY') return;
    const group = summary.parentElement;
    if (!(group instanceof HTMLDetailsElement)) return;

    event.preventDefault();
    this.#categoryTrigger = trigger;
    trigger.focus({ preventScroll: true });
    const request = ++this.#categoryRequest;
    const openGroup = () => {
      if (!this.isConnected || !this.isOpen || request !== this.#categoryRequest) return;
      if (!group.open) summary.click();
      this.#syncCategoryLinks();
      const panel = group.querySelector('.menu-drawer__submenu');
      if (panel instanceof HTMLElement) {
        this.#categoryFrame = requestAnimationFrame(() => {
          this.#categoryFrame = 0;
          onAnimationEnd(panel, () => {
            if (!this.isConnected || !this.isOpen || !group.open || request !== this.#categoryRequest) return;
            trapFocus(panel);
            const firstControl = panel.querySelector('button, a[href], summary');
            if (firstControl instanceof HTMLElement) firstControl.focus({ preventScroll: true });
          }, { subtree: false });
        });
      }
    };

    if (this.isOpen) {
      openGroup();
      return;
    }

    this.refs.details.querySelector('summary')?.click();
    this.#categoryFrame = requestAnimationFrame(() => {
      this.#categoryFrame = 0;
      onAnimationEnd(this.refs.menuDrawer, openGroup, { subtree: false });
    });
  };

  #syncCategoryLinks = () => {
    const links = this.#categoryHeader?.querySelectorAll('[data-mobile-menu-handle]') ?? [];
    for (const link of links) {
      if (!(link instanceof HTMLElement)) continue;
      const summary = this.querySelector('#HeaderDrawer-' + CSS.escape(link.dataset.mobileMenuHandle ?? ''));
      const group = summary?.parentElement;
      link.setAttribute('aria-expanded', String(this.isOpen && group instanceof HTMLDetailsElement && group.open));
    }
  };


  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#categoryHeader = this.closest('header-component');
    this.#categoryHeader?.addEventListener('click', this.#onCategoryClick);
    this.addEventListener('toggle', this.#syncCategoryLinks, true);
    this.#syncCategoryLinks();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#categoryHeader?.removeEventListener('click', this.#onCategoryClick);
    this.removeEventListener('toggle', this.#syncCategoryLinks, true);
    cancelAnimationFrame(this.#categoryFrame);
    this.#categoryRequest++;
    this.#categoryHeader = null;
    this.#categoryTrigger = null;
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const details = this.#getDetailsElement(event);
    this.#close(this.#categoryTrigger && !details.open ? this.refs.details : details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#categoryRequest++;
      cancelAnimationFrame(this.#categoryFrame);
    }
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          this.#categoryTrigger?.focus({ preventScroll: true });
          this.#categoryTrigger = null;
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
          if (this.#categoryTrigger) summary.focus({ preventScroll: true });
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
